// Reader ("gateway" in code): the list page, its filters, the reader page, its cards and drawers, the dialogs.
import type { CommandStatus } from '@lib/api/common';
import type { GatewayCondition, GatewayIssue, GatewaySort } from '@lib/api/gateways';

export const READERS = {
  title: 'Reader',
  sub: 'Perangkat pembaca tag di setiap Titik Distribusi.',
  noun: 'Reader',
  loading: 'Memuat Reader…',
  columns: { reader: 'Reader', place: 'Titik Distribusi', condition: 'Kondisi', tags: 'Tag terbaca', queue: 'Antrean data', version: 'Versi', lastSeen: 'Kabar terakhir' },
  unplaced: 'Belum ditempatkan',
  /** a reader that never sent a heartbeat: its last contact */
  never: 'Belum pernah',
  lastSeenTitle: (at: string) => `Kabar terakhir ${at}`,
  queueTitle: (n: string) => `${n} data menunggu dikirim ke sistem`,
  noQueue: 'Belum ada kabar dari Reader',
  empty: {
    title: 'Belum ada Reader terhubung',
    text: (warehouse: string) => `Reader tampil di sini setelah tersambung; tempatkan mulai dari ${warehouse}.`,
  },
  noMatch: 'Tidak ada Reader yang sesuai dengan filter.',
  /** a tab without readers (and no other filter) */
  tabEmpty: { attention: 'Tidak ada Reader yang perlu perhatian.', offline: 'Tidak ada Reader yang terputus.', never_connected: 'Semua Reader sudah pernah terhubung.', unplaced: 'Semua Reader sudah ditempatkan.' },
  unplacedBanner: (n: string) => `${n} Reader belum ditempatkan. Pembacaannya belum dihitung di Titik Distribusi mana pun.`,
  place: 'Tempatkan',
} as const;

/** The server's condition of a reader. */
export const CONDITION_LABEL: Record<GatewayCondition, string> = {
  normal: 'Normal',
  attention: 'Perlu perhatian',
  offline: 'Terputus',
  never_connected: 'Belum pernah terhubung',
};

/** Why a reader needs a look: a short label (badges, the list) */
export const ISSUE_LABEL: Record<GatewayIssue, string> = {
  OFFLINE: 'Terputus',
  NOT_RESPONDING: 'Tidak merespons',
  READER_ERROR: 'Gangguan pembacaan',
  CLOCK_UNSYNCED: 'Jam belum sinkron',
  OUTBOX_BACKLOG: 'Pengiriman data tertunda',
  UPDATE_FAILED: 'Pembaruan gagal',
};

/** ...and in a sentence (the reader page's banner). `since` is a WIB time, `pending` a number of data. */
export const ISSUE_SENTENCE = {
  OFFLINE: (since: string | null) =>
    since ? `Tidak ada kabar sejak ${since}. Data tetap tersimpan di Reader dan terkirim setelah tersambung kembali.` : 'Reader tidak tersambung. Data tetap tersimpan di Reader dan terkirim setelah tersambung kembali.',
  NOT_RESPONDING: (since: string | null) => (since ? `Reader tersambung, tetapi tidak mengirim kabar sejak ${since}.` : 'Reader tersambung, tetapi belum mengirim kabar.'),
  READER_ERROR: 'Pembacaan tag terganggu. Periksa sambungan modul pembaca dan antenanya.',
  CLOCK_UNSYNCED: 'Jam perangkat belum sinkron, sehingga waktu pembacaan dapat bergeser.',
  OUTBOX_BACKLOG: (pending: string | null) => (pending ? `${pending} data pembacaan menunggu dikirim ke sistem.` : 'Data pembacaan menunggu dikirim ke sistem.'),
  UPDATE_FAILED: 'Pembaruan perangkat lunak terakhir gagal dipasang.',
  never: 'Reader ini belum pernah mengirim kabar ke sistem.',
  /** after the sentences, for an account that may send commands while they cannot be sent */
  commandsLater: 'Perintah ke Reader dapat dikirim setelah Reader tersambung.',
} as const;

/** Status tabs over the list (counts from GET /gateways/summary). */
export const READER_TABS = {
  label: 'Kondisi Reader',
  all: 'Semua',
  attention: 'Perlu perhatian',
  offline: 'Terputus',
  never: 'Belum pernah terhubung',
  unplaced: 'Belum ditempatkan',
  name: (label: string, n: string) => `${label}, ${n} Reader`,
} as const;

/** The toolbar's search and menus (a sheet on phones). */
export const READER_FILTERS = {
  search: 'Cari Reader',
  searchPlaceholder: 'Cari nama, ID perangkat, atau Titik Distribusi',
  searchPlaceholderShort: 'Cari Reader',
  region: 'Wilayah',
  place: 'Titik Distribusi',
  allPlaces: 'Semua Titik Distribusi',
  placeSearch: 'Cari nama atau kode',
  noPlace: 'Tidak ada Titik Distribusi yang sesuai.',
  placesFailed: 'Titik Distribusi belum dapat dimuat.',
  morePlaces: 'Ketik nama atau kode untuk mempersempit.',
  version: 'Versi',
  allVersions: 'Semua versi',
  noVersions: 'Belum ada versi.',
  versionsFailed: 'Daftar versi belum dapat dimuat.',
  versionsPicked: (n: number) => `${n} versi`,
  sort: 'Urutkan',
} as const;

/** Urutkan: the menu's options, and the short word on its button. */
export const READER_SORT: Record<GatewaySort, { label: string; short: string }> = {
  condition: { label: 'Kondisi, yang perlu perhatian lebih dahulu', short: 'Kondisi' },
  name: { label: 'Nama, A–Z', short: 'Nama' },
  lastSeen: { label: 'Kabar terakhir, yang terlama lebih dahulu', short: 'Kabar terakhir' },
};

/** The ⋯ menu of a row, the reader page's actions, the confirmation and the toasts. */
export const READER_ACTIONS = {
  menu: (name: string) => `Tindakan untuk ${name}`,
  more: 'Tindakan lainnya',
  open: 'Lihat rincian',
  place: 'Tempatkan',
  move: 'Pindahkan',
  edit: 'Ubah nama dan penempatan',
  beep: 'Bunyikan reader',
  check: 'Periksa reader',
  power: 'Ubah daya antena',
  remove: 'Hapus dari daftar',
  confirmTitle: (name: string) => `Hapus ${name} dari daftar?`,
  confirmBody: 'Reader ini tidak lagi tampil di daftar. Riwayat pembacaannya tetap tersimpan. Jika Reader tersambung kembali, Reader akan tampil lagi di daftar.',
  confirmLabel: 'Hapus dari daftar',
  removed: (name: string) => `${name} dihapus dari daftar.`,
  commandOk: (label: string, name: string) => `${label} (${name}) berhasil dijalankan.`,
  commandSent: (label: string, name: string) => `${label} (${name}) terkirim. Reader belum menjawab.`,
  commandFailed: (label: string, name: string, error: string | null) => (error ? `${label} (${name}) gagal: ${error}` : `${label} (${name}) gagal.`),
  /** a command the dashboard has no words for */
  otherCommand: 'Perintah lain',
} as const;

/** What a reader sent: its events (the reader page's own words for the device's event types). */
export const READER_EVENT_LABEL: Record<string, string> = {
  TAG_SEEN: 'Tag terbaca',
  TAG_GONE: 'Tag keluar jangkauan',
  TAG_TID: 'TID terbaca',
  READER_STATUS: 'Status modul pembaca',
  GATEWAY_START: 'Reader dinyalakan',
};
/** an event type the dashboard has no words for */
export const OTHER_EVENT = 'Kejadian lain';

export const COMMAND_STATE: Record<CommandStatus, string> = { sent: 'Menunggu', ok: 'Berhasil', error: 'Gagal' };

/** The reader page. */
export const READER_DETAIL = {
  missing: 'Reader tidak ditemukan',
  back: 'Kembali ke daftar Reader',
  unplaced: 'Belum ditempatkan',
  placeBanner: 'Tempatkan Reader ini di sebuah Titik Distribusi agar pembacaannya dipakai untuk memuat dan menerima Surat Jalan di sana.',
  seeAlerts: (n: string) => `Lihat ${n} peringatan`,
  seeAll: 'Lihat semua',
  seeAllCount: (n: string) => `Lihat semua (${n})`,
  condition: {
    title: 'Kondisi perangkat',
    groups: { connection: 'Koneksi', reading: 'Pembacaan', delivery: 'Pengiriman data', device: 'Perangkat' },
    status: 'Status',
    lastSeen: 'Kabar terakhir',
    upSince: 'Menyala sejak',
    reader: 'Modul pembaca',
    power: 'Daya antena',
    freq: 'Frekuensi',
    present: 'Tag di depan Reader',
    tidFailures: 'Gagal membaca identitas tag',
    queue: 'Menunggu dikirim',
    intact: 'Diterima lengkap hingga nomor',
    version: 'Versi',
    firmware: 'Firmware modul pembaca',
    clock: 'Jam',
    cpu: 'Suhu CPU',
    connected: 'Tersambung',
    disconnected: 'Terputus',
    neverConnected: 'Belum pernah terhubung',
    readerOk: 'Berfungsi',
    readerFault: 'Terganggu',
    queueEmpty: 'Tidak ada',
    queueN: (n: string) => `${n} data`,
    synced: 'Sinkron',
    unsynced: 'Belum sinkron',
    noHeartbeat: 'Belum ada kabar dari Reader. Data kondisi tampil setelah kabar pertama diterima.',
  },
  alerts: { title: 'Peringatan aktif', empty: 'Tidak ada peringatan aktif untuk Reader ini.' },
  events: { title: 'Pembacaan terakhir', empty: 'Belum ada pembacaan dari Reader ini.', drawerTitle: (name: string) => `Pembacaan ${name}`, noun: 'pembacaan' },
  commands: { title: 'Riwayat perintah', empty: 'Belum ada perintah ke Reader ini.', drawerTitle: (name: string) => `Riwayat perintah ${name}`, noun: 'perintah' },
  update: {
    title: 'Pembaruan perangkat lunak',
    version: 'Versi terpasang',
    state: 'Status pembaruan',
    noUpdate: 'Tidak ada pembaruan',
    history: 'Riwayat terakhir',
    historyEmpty: 'Belum ada riwayat pembaruan.',
    open: 'Buka Pembaruan',
  },
  gaps: {
    title: 'Pemulihan data',
    empty: 'Semua data dari Reader ini diterima utuh.',
    range: (from: string, to: string) => (from === to ? `Data nomor ${from}` : `Data nomor ${from}–${to}`),
    attempts: (n: string) => `${n} kali dicoba`,
    pending: 'Diproses',
    recovered: 'Pulih',
    lost: 'Tidak tersedia',
    drawerTitle: (name: string) => `Pemulihan data ${name}`,
    noun: 'permintaan',
  },
} as const;

/** Name a reader and put it at a Titik Distribusi. */
export const PLACE_DIALOG = {
  titlePlace: (name: string) => `Tempatkan ${name}`,
  titleMove: (name: string) => `Pindahkan ${name}`,
  titleEdit: 'Ubah nama dan penempatan',
  label: 'Nama tampilan',
  labelHelp: 'Contoh: Pintu gudang. Tampil di layar petugas sebagai “Reader pintu gudang”.',
  labelPlaceholder: 'Pintu gudang',
  place: 'Titik Distribusi',
  placeHelp: 'Tabung yang terbaca Reader ini dihitung di Titik Distribusi tersebut.',
  none: 'Belum ditempatkan',
  save: 'Simpan',
  placed: (name: string, place: string) => `${name} ditempatkan di ${place}.`,
  saved: 'Reader diperbarui.',
} as const;

export const POWER_DIALOG = {
  title: 'Ubah daya antena',
  field: 'Daya (dBm)',
  help: 'Batas di Indonesia: 27 dBm untuk antena 8 dBi. Daya yang lebih rendah memperpendek jarak baca.',
  invalid: (min: number, max: number) => `Isi bilangan bulat ${min}–${max}.`,
  submit: 'Kirim ke Reader',
} as const;

/** One line about the reader at a place (operator screens). */
export const READER_LINE = {
  unknown: 'Status Reader belum diketahui',
  none: 'Belum ada Reader di Titik Distribusi ini',
  name: (label: string | null, gwId: string) => `Reader ${label ?? gwId}`,
  ok: (name: string) => `${name} terhubung`,
  notOk: (name: string, condition: string) => `${name} ${condition.toLowerCase()}`,
} as const;
